import { Collapse, Empty, Spin, Tag, Typography, Tooltip } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useGetAnswerAuditLogs } from "@hooks";
import { ExamAnswerAuditLog } from "@types";

interface Props {
  examInstanceId: string;
  /**
   * Smije li trenutni korisnik vidjeti audit zapise. Renderiranje je dodatno
   * gateirano i u nadređenoj komponenti (samo Admin/Assessor), a ovdje služi i
   * kao zaštita protiv nepotrebnog mrežnog poziva.
   */
  canView: boolean;
}

/**
 * Prikaz trajnih (append-only) audit zapisa predaja odgovora polaznika za
 * potrebe oporavka. Lijeno učitava podatke tek kad administrator otvori panel.
 * Vidljivo ISKLJUČIVO admin i assessor rolama.
 */
export const AnswerAuditPanel = ({ examInstanceId, canView }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { data: logs, isLoading, isError } = useGetAnswerAuditLogs(examInstanceId, {
    enabled: canView && open,
  });

  if (!canView) {
    return null;
  }

  const parseAnswers = (answersJson?: string): string => {
    if (!answersJson) return "-";
    try {
      const parsed = JSON.parse(answersJson) as Array<{
        Value?: string;
        PairAnswerText?: string;
      }>;
      if (!Array.isArray(parsed) || parsed.length === 0) return "-";
      return parsed
        .map((a) => {
          const value = a.Value ?? "";
          const pair = a.PairAnswerText ? ` → ${a.PairAnswerText}` : "";
          return `${value}${pair}`.trim();
        })
        .filter(Boolean)
        .join(" | ") || "-";
    } catch {
      return answersJson;
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div style={{ textAlign: "center", padding: 16 }}>
          <Spin />
        </div>
      );
    }

    if (isError) {
      return (
        <Typography.Text type="danger">
          {t("pages.activity.answerAudit.loadError")}
        </Typography.Text>
      );
    }

    if (!logs || logs.length === 0) {
      return (
        <Empty
          description={t("pages.activity.answerAudit.empty")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <Collapse
        size="small"
        items={logs.map((log: ExamAnswerAuditLog) => ({
          key: log.id,
          label: (
            <span>
              <Typography.Text strong>
                {dayjs(log.submittedAtUtc).format("DD.MM.YYYY HH:mm:ss")}
              </Typography.Text>
              {"  "}
              <Tag color={log.isSubmittedManually ? "blue" : "default"}>
                {log.isSubmittedManually
                  ? t("pages.activity.answerAudit.manual")
                  : t("pages.activity.answerAudit.auto")}
              </Tag>
              <Tag>{t("pages.activity.answerAudit.answerCount", { count: log.answerCount })}</Tag>
            </span>
          ),
          children: (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div>
                <Typography.Text strong>
                  {t("pages.activity.answerAudit.answers")}:{" "}
                </Typography.Text>
                <Typography.Text>{parseAnswers(log.answersJson)}</Typography.Text>
              </div>
              {log.payloadJson && (
                <details>
                  <summary style={{ cursor: "pointer" }}>
                    {t("pages.activity.answerAudit.rawPayload")}
                  </summary>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      background: "#f5f5f5",
                      padding: 8,
                      borderRadius: 4,
                      fontSize: 12,
                      marginTop: 6,
                    }}
                  >
                    {log.payloadJson}
                  </pre>
                </details>
              )}
            </div>
          ),
        }))}
      />
    );
  };

  return (
    <div>
      <Tooltip title={t("pages.activity.answerAudit.tooltip")}>
        <Typography.Text strong>
          {t("pages.activity.answerAudit.title")}
        </Typography.Text>
      </Tooltip>
      <div style={{ marginTop: 8 }}>
        <Collapse
          size="small"
          activeKey={open ? ["panel"] : []}
          onChange={(keys) => setOpen((keys as string[]).includes("panel"))}
          items={[
            {
              key: "panel",
              label: t("pages.activity.answerAudit.show"),
              children: renderBody(),
            },
          ]}
        />
      </div>
    </div>
  );
};
